import React, { useState, useEffect, useCallback } from "react";
import config from "../../common/Config";
import { toast } from "sonner";
import { pushQuickBooksMaster, resolveQuickBooksEnabled } from "../../../utils/quickBooksTenant";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid2,
  Checkbox,
  FormControlLabel,
  TablePagination,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Switch,
  Skeleton,
  Chip,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Iconify } from '../../Minimal/src/components/iconify/iconify';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { parse, format, isValid } from 'date-fns';
import '../../common/commonStyles.css';
import { postGetSupplierList, axiosPostSupplierMultipart } from "../../../utils/supplierApi";

const Supplier = () => {
  const [searchValue, setSearchValue] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [errors, setErrors] = useState({});
  const accessToken = localStorage.getItem("accessToken");
  const [showQuickBooks, setShowQuickBooks] = useState(false);
  const [pushingQb, setPushingQb] = useState(false);
  const [addSupplierDialogOpen, setAddSupplierDialogOpen] = useState(false);
  const [isDisable, setIsDisable] = useState(false);
  const [totalSuppliers, setTotalSuppliers] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [menuAnchor, setMenuAnchor] = useState({ el: null, row: null });
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const company_tax_type = localStorage.getItem("company_tax_type") || "";
  const token = localStorage.getItem("token");

  const fetchData = useCallback(async () => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    setLoading(true);
    try {
      const json = await postGetSupplierList(`${config.API_URL}/get_supplier_list`, token, {
        draw: page + 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        searchValue: searchValue || "",
      });

      if (json?.data && Array.isArray(json.data)) {
        setSuppliers(json.data);
        setTotalSuppliers(json.recordsFiltered || json.recordsTotal || json.data.length);
      } else {
        setSuppliers([]);
        setTotalSuppliers(0);
      }
    } catch (error) {
      toast.error("Failed to fetch suppliers");
      setSuppliers([]);
      setTotalSuppliers(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchValue, accessToken, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    resolveQuickBooksEnabled().then(setShowQuickBooks);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setSearchValue(value);
    setPage(0);
  };

  const handlePushQuickBooks = async () => {
    if (pushingQb) return;
    setPushingQb(true);
    try {
      const data = await pushQuickBooksMaster("vendors");
      toast.success(data.message || data.msg || "Suppliers pushed to QuickBooks");
    } catch (e) {
      toast.error(e.message || "Push failed");
    } finally {
      setPushingQb(false);
    }
  };

  const handleChangeStatus = async (id, status) => {
    const formData = new FormData();
    formData.append("status", status);
    formData.append("id", id);
    const apiUrl = `${config.API_URL}/update_supplier_status`;
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Failed to change status");
      }
      const data = await response.json();
      toast.success("Status changed successfully.");
      fetchData();
    } catch (error) {
      toast.error("Failed to change status.");
    }
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setMenuAnchor({ el: null, row: null });
  };

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setMenuAnchor({ el: event.currentTarget, row });
  };

  const handleMenuAction = (action, row, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    handleMenuClose();
    if (action === 'edit') {
      window.location.href = `${config.BASE_URL}/supplier/edit_supplier/${row.id}`;
    }
  };

  const [formData, setFormData] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    contact_person: "",
    contact_person_mob: "",
    contact_person_email: "",
    finance_person: "",
    finance_person_mob: "",
    tobacco: "",
    alcohol: "",
    tpd_license_validity: "",
    awrs_license_validity: "",
  });

  const validateForm = () => {
    const newErrors = {};

    // Company validation
    if (!formData.company) {
      newErrors.company = "Mention company Name.";
    } else if (formData.company.length < 2) {
      newErrors.company = "Company name must consist of at least 2 characters.";
    } else if (formData.company.length > 255) {
      newErrors.company = "Company name must not exceed 255 characters.";
    }

    // Name validation
    if (!formData.name) {
      newErrors.name = "Mention Name.";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must consist of at least 2 characters.";
    } else if (formData.name.length > 255) {
      newErrors.name = "Name must not exceed 255 characters.";
    }

    // Phone validation
    if (!formData.phone) {
      newErrors.phone = "Phone is Required.";
    } else if (!/^\d{10,12}$/.test(formData.phone)) {
      newErrors.phone = "Phone number must be between 10 and 12 digits.";
    }

    // Contact person validation
    if (!formData.contact_person) {
      newErrors.contact_person = "Mention person Name.";
    } else if (formData.contact_person.length < 2) {
      newErrors.contact_person =
        "Contact person name must consist of at least 2 characters.";
    } else if (formData.contact_person.length > 255) {
      newErrors.contact_person =
        "Contact person name must not exceed 255 characters.";
    }

    // Contact person mobile validation
    if (!formData.contact_person_mob) {
      newErrors.contact_person_mob = "Contact person mobile is Required.";
    } else if (!/^\d{10,12}$/.test(formData.contact_person_mob)) {
      newErrors.contact_person_mob =
        "Contact person mobile must be between 10 and 12 digits.";
    }

    // Finance person validation
    if (!formData.finance_person) {
      newErrors.finance_person = "Mention Finance Person Name.";
    } else if (formData.finance_person.length < 2) {
      newErrors.finance_person =
        "Finance person name must consist of at least 2 characters.";
    } else if (formData.finance_person.length > 255) {
      newErrors.finance_person =
        "Finance person name must not exceed 255 characters.";
    }

    if (!formData.finance_person_mob) {
      newErrors.finance_person_mob = "finance mobile is Required.";
    } else if (!/^\d{10,12}$/.test(formData.finance_person_mob)) {
      newErrors.finance_person_mob =
        "finance person mobile must be between 10 and 12 digits.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    let fieldErrors = {};

    // Validate the input field immediately as the user types
    switch (name) {
      case "company":
        if (!value) {
          fieldErrors.company = "Mention company Name.";
        } else if (value.length < 2) {
          fieldErrors.company =
            "Company name must consist of at least 2 characters.";
        } else if (value.length > 255) {
          fieldErrors.company = "Company name must not exceed 255 characters.";
        }

        // Clear error if all conditions are met
        if (!fieldErrors.company) {
          fieldErrors.company = "";
        }
        break;

      case "name":
        if (!value) {
          fieldErrors.name = "Mention Name.";
        } else if (value.length < 2) {
          fieldErrors.name = "Name must consist of at least 2 characters.";
        } else if (value.length > 255) {
          fieldErrors.name = "Name must not exceed 25 characters.";
        }
        if (!fieldErrors.name) {
          fieldErrors.name = "";
        }
        break;

      case "phone":
        if (!value) {
          fieldErrors.phone = "Phone is Required.";
        } else if (!/^\d{10,12}$/.test(value)) {
          fieldErrors.phone = "Phone number must be between 10 and 12 digits.";
        }
        if (!fieldErrors.phone) {
          fieldErrors.phone = "";
        }
        break;

      case "contact_person":
        if (!value) {
          fieldErrors.contact_person = "Mention person Name.";
        } else if (value.length < 2) {
          fieldErrors.contact_person =
            "Contact person name must consist of at least 2 characters.";
        } else if (value.length > 255) {
          fieldErrors.contact_person =
            "Contact person name must not exceed 255 characters.";
        }
        if (!fieldErrors.contact_person) {
          fieldErrors.contact_person = "";
        }
        break;

      case "contact_person_mob":
        if (!value) {
          fieldErrors.contact_person_mob = "Contact person mobile is Required.";
        } else if (!/^\d{10,12}$/.test(value)) {
          fieldErrors.contact_person_mob =
            "Contact person mobile must be between 10 and 12 digits.";
        }
        if (!fieldErrors.contact_person_mob) {
          fieldErrors.contact_person_mob = "";
        }
        break;

      case "finance_person":
        if (!value) {
          fieldErrors.finance_person = "Mention Finance Person Name.";
        } else if (value.length < 2) {
          fieldErrors.finance_person =
            "Finance person name must consist of at least 2 characters.";
        } else if (value.length > 255) {
          fieldErrors.finance_person =
            "Finance person name must not exceed 255 characters.";
        }
        if (!fieldErrors.finance_person) {
          fieldErrors.finance_person = "";
        }
        break;

      case "finance_person_mob":
        if (!value) {
          fieldErrors.finance_person_mob = "Contact person mobile is Required.";
        } else if (!/^\d{10,12}$/.test(value)) {
          fieldErrors.finance_person_mob =
            "Contact person mobile must be between 10 and 12 digits.";
        }
        if (!fieldErrors.finance_person_mob) {
          fieldErrors.finance_person_mob = "";
        }
        break;

      default:
        break;
    }

    // Update errors state for the specific field
    setErrors((prevErrors) => ({
      ...prevErrors,
      ...fieldErrors,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? "1" : "0",
    }));
  };

  const submitSupplierForm = async () => {
    setIsDisable(true);
    if (!validateForm()) {
      setIsDisable(false);
      return;
    }
    const form = document.getElementById("addsupplierform");

    const formDataToSubmit = new FormData(form);
    try {
      const result = await axiosPostSupplierMultipart(
        `${config.API_URL}/add_supplier`,
        accessToken,
        formDataToSubmit
      );
      if (result.success === true) {
        toast.success("Supplier added successfully.");
        setIsDisable(false);
        setAddSupplierDialogOpen(false);
        setFormData({
          company: "",
          name: "",
          email: "",
          phone: "",
          contact_person: "",
          contact_person_mob: "",
          contact_person_email: "",
          finance_person: "",
          finance_person_mob: "",
          tobacco: "",
          alcohol: "",
          tpd_license_validity: "",
          awrs_license_validity: "",
        });
        setErrors({});
        fetchData();
      } else {
        toast.error("Supplier add Failed. Try Again");
        setIsDisable(false);
      }
    } catch (error) {
      if (error.message === 'ENCRYPT_KEY_MISSING') {
        toast.error('Encryption key missing.');
      } else {
        toast.error("Failed to add supplier.");
      }
      setIsDisable(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleCloseDialog = () => {
    setAddSupplierDialogOpen(false);
    setFormData({
      company: "",
      name: "",
      email: "",
      phone: "",
      contact_person: "",
      contact_person_mob: "",
      contact_person_email: "",
      finance_person: "",
      finance_person_mob: "",
      tobacco: "",
      alcohol: "",
    });
    setErrors({});
  };

  return (
    <Box>
      <Dialog
        open={addSupplierDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add Supplier
          </Typography>
          <IconButton onClick={handleCloseDialog}>
            <Iconify icon="eva:close-fill" width={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 0 }}>
          <Box component="form" id="addsupplierform">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, pb: 2 }}>Company Detail</Typography>
            <Grid2 container spacing={2} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <TextField
                    className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Company"
                  name="company"
                  id="company"
                  value={formData.company}
                  onChange={handleChange}
                  error={!!errors.company}
                  helperText={errors.company}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <TextField
                         className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Name"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <TextField
                  className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Email Address"
                  name="email"
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <TextField
                 className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Phone"
                  name="phone"
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  required
                  inputProps={{
                    style: {
                      appearance: "none",
                      MozAppearance: "textfield",
                    }
                  }}
                />
              </Grid2>
            </Grid2>

            <Grid2 container spacing={2} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12, sm: 4 }}>
                <TextField
                  className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Address"
                  name="address"
                  id="address"
                  multiline
                  rows={1}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 2 }}>
                <TextField
                    className="input-border text-field"
                  fullWidth
                  size="small"
                  label="City"
                  name="city"
                  id="city"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 2 }}>
                <TextField
                        className="input-border text-field"
                  fullWidth
                  size="small"
                  label="State"
                  name="state"
                  id="state"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 2 }}>
                <TextField
                   className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Country"
                  name="country"
                  id="country"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 2 }}>
                <TextField
                    className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Postal Code"
                  name="postal_code"
                  id="postal_code"
                />
              </Grid2>
            </Grid2>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, my: 1 }}>Contact Person</Typography>
            <Grid2 container spacing={2} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12, sm: 4 }}>
                <TextField
                     className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Name"
                  name="contact_person"
                  id="contact_person"
                  onChange={handleChange}
                  error={!!errors.contact_person}
                  helperText={errors.contact_person}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 4 }}>
                <TextField
                       className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Phone"
                  name="contact_person_mob"
                  id="contact_person_mob"
                  type="tel"
                  value={formData.contact_person_mob}
                  onChange={handleChange}
                  error={!!errors.contact_person_mob}
                  helperText={errors.contact_person_mob}
                  required
                  inputProps={{
                    style: {
                      appearance: "none",
                      MozAppearance: "textfield",
                    }
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 4 }}>
                <TextField
                     className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Email"
                  name="contact_person_email"
                  id="contact_person_email"
                  type="email"
                  value={formData.contact_person_email}
                  onChange={handleChange}
                />
              </Grid2>
            </Grid2>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, my: 1 }}>Banking & Finance Details</Typography>
            <Grid2 container spacing={2} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <TextField
                      className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Financial Person Name"
                  name="finance_person"
                  id="finance_person"
                  value={formData.finance_person}
                  onChange={handleChange}
                  error={!!errors.finance_person}
                  helperText={errors.finance_person}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <TextField
                className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Financial Person Mobile"
                  name="finance_person_mob"
                  id="finance_person_mob"
                  type="tel"
                  value={formData.finance_person_mob}
                  onChange={handleChange}
                  error={!!errors.finance_person_mob}
                  helperText={errors.finance_person_mob}
                  required
                  inputProps={{
                    style: {
                      appearance: "none",
                      MozAppearance: "textfield",
                    }
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <TextField
                  className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Financial Person Email"
                  name="finance_person_email"
                  id="finance_person_email"
                  type="email"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <TextField
                  className="form-input-border"
                  fullWidth
                  size="small"
                  label={`${company_tax_type} Number`}
                  name="vat_no"
                  id="vat_no"
                />
              </Grid2>
            </Grid2>

            <Typography sx={{ fontSize: '16px', fontWeight: 500, my: 0.5 }}>Banking Details</Typography>
            <Grid2 container spacing={2} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <TextField
             className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Bank Account Number"
                  name="supplier_account_number"
                  id="supplier_account_number"
                  value={formData.supplier_account_number}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <TextField
                  className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Branch Name"
                  name="supplier_branch_name"
                  id="supplier_branch_name"
                  value={formData.supplier_branch_name}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <TextField
                    className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Bank Name"
                  name="supplier_bank_name"
                  id="supplier_bank_name"
                  value={formData.supplier_bank_name}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <TextField
               className="input-border text-field"
                  fullWidth
                  size="small"
                  label="Branch Code"
                  name="supplier_branch_code"
                  id="supplier_branch_code"
                  value={formData.supplier_branch_code}
                  onChange={handleChange}
                />
              </Grid2>
            </Grid2>

            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Licence Details</Typography>
            <Grid2 container spacing={2} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      className="checkBox"
                      name="tobacco"
                      checked={formData.tobacco === "1"}
                      onChange={handleCheckboxChange}
                      value="1"
                    />
                  }
                  label="Tobacco Product"
                />
                {formData.tobacco === "1" && (
                  <Box sx={{ mt: 2, p: 2, border: '1px solid rgba(19, 20, 20, 0.62)', borderRadius: 1 }}>
                    <Grid2 container spacing={2}>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                           className="input-border text-field"
                          fullWidth
                          size="small"
                          label="TPD License Number"
                          name="tpd_license_no"
                          id="tpd_license_no"
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                           className="form-input-border"
                            label="TPD License Validity Till Date"
                            value={formData.tpd_license_validity ? (isValid(parse(formData.tpd_license_validity, 'yyyy-MM-dd', new Date())) ? parse(formData.tpd_license_validity, 'yyyy-MM-dd', new Date()) : null) : null}
                            onChange={(newValue) => {
                              const formattedDate = newValue ? format(newValue, 'yyyy-MM-dd') : '';
                              setFormData({ ...formData, tpd_license_validity: formattedDate });
                            }}
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                                name: 'tpd_license_validity',
                                id: 'tpd_license_validity',
                              }
                            }}
                          />
                        </LocalizationProvider>
                        <input type="hidden" name="tpd_license_validity" value={formData.tpd_license_validity} />
                      </Grid2>
                    </Grid2>
                  </Box>
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      className="checkBox"
                      name="alcohol"
                      checked={formData.alcohol === "1"}
                      onChange={handleCheckboxChange}
                      value="1"
                    />
                  }
                  label="Alcohol Licence"
                />
                {formData.alcohol === "1" && (
                  <Box sx={{ mt: 2, p: 2, border: '1px solid rgba(19, 20, 20, 0.62)', borderRadius: 1 }}>
                    <Grid2 container spacing={2}>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                       className="input-border text-field"
                          fullWidth
                          size="small"
                          label="AWRS License Number"
                          name="awrs_license_no"
                          id="awrs_license_no"
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                              className="input-border text-field"
                            label="AWRS License Validity Till Date"
                            value={formData.awrs_license_validity ? (isValid(parse(formData.awrs_license_validity, 'yyyy-MM-dd', new Date())) ? parse(formData.awrs_license_validity, 'yyyy-MM-dd', new Date()) : null) : null}
                            onChange={(newValue) => {
                              const formattedDate = newValue ? format(newValue, 'yyyy-MM-dd') : '';
                              setFormData({ ...formData, awrs_license_validity: formattedDate });
                            }}
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                                name: 'awrs_license_validity',
                                id: 'awrs_license_validity',
                              }
                            }}
                          />
                        </LocalizationProvider>
                        <input type="hidden" name="awrs_license_validity" value={formData.awrs_license_validity} />
                      </Grid2>
                    </Grid2>
                  </Box>
                )}
              </Grid2>
            </Grid2>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 3 }}>
          <Button onClick={handleCloseDialog}
            variant="outlined"
            className="button-variant-outlined"
            sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitSupplierForm}
            className="button-variant-contained"
            disabled={isDisable}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              px: 2,
            }}
          >
            {isDisable ? 'Adding Supplier...' : 'Add Supplier'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* <Header /> */}
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography className="page-title">
                  Supplier
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 8 }} gap={1} display="flex" flexDirection={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} justifyContent="flex-end" flexWrap="nowrap" mt={{ xs: 2, md: 0 }}>
                <TextField
                  className="input-border"
                  size="small"
                  placeholder="Search suppliers..."
                  value={searchInputValue}
                  onChange={handleSearchChange}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline"/>
                        </InputAdornment>
                      )
                    }
                  }}
                  sx={{
                    width: { xs: '100%', md: 220 },
                    minWidth: { md: 180 },
                    flexShrink: 1,
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => setAddSupplierDialogOpen(true)}
                  startIcon={<Iconify icon="mingcute:add-line" width={18} />}
                  className="button-variant-contained"
                  sx={{
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  Add Supplier
                </Button>
                {showQuickBooks && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    disabled={pushingQb}
                    onClick={handlePushQuickBooks}
                    sx={{
                      color: "#000",
                      borderColor: "#000",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      height: 34,
                      "&:hover": {
                        borderColor: "#000",
                        backgroundColor: "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    {pushingQb ? "Pushing..." : "Push to QuickBooks"}
                  </Button>
                )}
              </Grid2>
            </Grid2>
          </Box>
          <TableContainer

          >
            <Table
              size="medium"
              className="table-cell-border"
              sx={{
                tableLayout: "auto"
              }}
            >
              <TableHead>
                <TableRow className="table-cell-header">
                  {[
                    "#",
                    "Company",
                    "Name",
                    "Email Address",
                    "Phone",
                    "City",
                    "State",
                    company_tax_type ? `${company_tax_type} Number` : "Vat Number",
                    "Status",
                    "Action"
                  ].map((header) => {
                    let align = "left";
                    if (header === "#" || header === "Status" || header === "Action") {
                      align = "center";
                    }
                    return (
                      <TableCell
                        key={header}
                        align={align}
                        className="table-cell-header"
                      >
                        {header}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={10}>
                        <Skeleton height={40} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : suppliers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography className="commonColor">
                        No records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  suppliers.map((row, index) => (
                    <TableRow
                      key={row.id}
                      hover
                      className="table-cell-style"
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(145, 158, 171, 0.04)"
                        },

                      }}
                    >
                      <TableCell align="center">
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell align="left">
                        {row.company}
                      </TableCell>
                      <TableCell align="left">
                        {row.name}
                      </TableCell>
                      <TableCell align="left">
                        {row.email ? row.email.toLowerCase() : ''}
                      </TableCell>
                      <TableCell align="left">
                        {row.phone}
                      </TableCell>
                      <TableCell align="left">
                        {row.city}
                      </TableCell>
                      <TableCell align="left">
                        {row.state}
                      </TableCell>
                      <TableCell align="left">
                        {row.vat_no}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          component="label"
                          sx={{
                            position: 'relative',
                            display: 'inline-flex',
                            alignItems: 'center',
                            width: 70,
                            height: 26,
                            cursor: 'pointer',
                            margin: '0 auto',
                          }}
                        >
                          <Box
                            component="input"
                            type="checkbox"
                            checked={row.status == "1"}
                            onChange={(e) => handleChangeStatus(row.id, e.target.checked ? 1 : 0)}
                            sx={{
                              opacity: 0,
                              width: 0,
                              height: 0,
                              position: 'absolute',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              height: '100%',
                              backgroundColor: row.status == "1" ? '#F8991D' : '#eceeef',
                              borderRadius: '13px',
                              transition: 'all 0.3s ease',
                              boxShadow: row.status == "1"
                                ? 'inset 0 1px 2px rgba(0, 0, 0, 0.15), inset 0 0 3px rgba(0, 0, 0, 0.2)'
                                : 'inset 0 1px 2px rgba(0, 0, 0, 0.12), inset 0 0 2px rgba(0, 0, 0, 0.15)',
                              overflow: 'hidden',
                            }}
                          >
                            {/* Toggle Handle */}
                            <Box
                              sx={{
                                position: 'absolute',
                                width: 18,
                                height: 18,
                                left: row.status == "1" ? 'calc(100% - 24px)' : '3px',
                                top: '3px',
                                backgroundColor: '#FFFFFF',
                                borderRadius: '50%',
                                transition: 'all 0.3s ease',
                                boxShadow: '1px 1px 5px rgba(0, 0, 0, 0.2)',
                                zIndex: 2,
                              }}
                            />
                            {/* ACTIVE Text */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '8px',
                                transform: 'translateY(-50%)',
                                color: '#FFFFFF',
                                fontSize: '9px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                opacity: row.status == "1" ? 1 : 0,
                                transition: 'opacity 0.3s ease',
                                textShadow: '0 1px rgba(0, 0, 0, 0.2)',
                                zIndex: 1,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              ACTIVE
                            </Box>
                            {/* INACTIVE Text */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                right: '8px',
                                transform: 'translateY(-50%)',
                                color: '#aaaaaa',
                                fontSize: '9px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                opacity: row.status == "1" ? 0 : 1,
                                transition: 'opacity 0.3s ease',
                                textShadow: '0 1px rgba(255, 255, 255, 0.5)',
                                zIndex: 1,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              INACTIVE
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1, fontSize: "0.75rem", color: "#212b36" }}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, row)}
                          sx={{
                            color: "#637381",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)"
                            }
                          }}
                        >
                          <Iconify icon="eva:more-vertical-fill" width={18} height={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {suppliers.length > 0 && (
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <TablePagination
                component="div"
                count={totalSuppliers}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                className="pagination-styles"
              />
            </Box>
          )}
        </Paper>
      </Box>

      <Menu
        anchorEl={menuAnchor.el}
        open={Boolean(menuAnchor.el)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            mt: 1,
          },
          onClick: (e) => e.stopPropagation(),
        }}
      >
        {menuAnchor.row && (
          <MenuItem onClick={(e) => {
            e.stopPropagation();
            handleMenuAction('edit', menuAnchor.row, e);
          }}>
            <ListItemIcon>
              <Iconify icon="fa-regular:edit" width={20} height={20} />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
      </Menu>

    </Box>
  );
};
export default Supplier;
